import { put, select, takeLatest, takeEvery } from 'redux-saga/effects';

import {
  cadViewer,
  geometricPartFile,
} from 'modules/common/viewer/cadViewer/data/actions';
import { pdfViewer } from 'modules/common/viewer/pdfViewer/data/actions';

import { ActionWithPayload } from 'store/types';

import { getFileExtension, isFilePDF } from 'utils/helpers';

import { getVisibleBodies } from '../cadViewer/data/reducer';
import {
  ReceiveActivePartFileUuidPayload,
  ReceivePartFilesPayload,
  types,
} from './actions';
import { getActiveFile } from './reducer';

export default function* root() {
  yield takeLatest(
    types.RECEIVE_ACTIVE_PART_FILE_UUID,
    watchReceiveActivePartFileUuid,
  );
  yield takeLatest(types.RESET, watchReset);
  yield takeEvery(types.RECEIVE_PART_FILES, watchReceivePartFiles);
}

function* watchReceivePartFiles(
  action: ActionWithPayload<
    typeof types.RECEIVE_PART_FILES,
    ReceivePartFilesPayload
  >,
) {
  try {
    const {
      entities: { partFiles },
      result,
    } = action.payload;

    const seenAssemblyParentFiles: Array<string | null | undefined> = [];
    const geometricFileUuids: string[] = [];
    result.forEach((fileUuid) => {
      const partFile = partFiles[fileUuid];
      if (partFile.hasGeometry) {
        if (!partFiles[fileUuid].hasAssemblyParentFile) {
          // add all non-assembly extracted parts
          geometricFileUuids.push(fileUuid);
        } else {
          if (partFile.isRootPart) {
            // add the root part
            geometricFileUuids.push(fileUuid);
          } else {
            const parentUuid = partFile.assemblyParentFileUuid;
            const isParentFileInResults =
              parentUuid != null && result.includes(parentUuid);
            if (
              !isParentFileInResults &&
              !seenAssemblyParentFiles.includes(parentUuid)
            ) {
              geometricFileUuids.push(fileUuid);
              seenAssemblyParentFiles.push(parentUuid);
            }
          }
        }
      }
    });

    if (geometricFileUuids.length) {
      yield put(
        geometricPartFile.bulkPrepare({ partFileUuids: geometricFileUuids }),
      );
    }
  } catch (e) {
    console.error('Error in watchReceivePartFiles', e);
  }
}

function* watchReset() {
  yield put(cadViewer.reset());
  yield put(pdfViewer.reset());
}

function* watchReceiveActivePartFileUuid(
  action: ActionWithPayload<
    typeof types.RECEIVE_ACTIVE_PART_FILE_UUID,
    ReceiveActivePartFileUuidPayload
  >,
) {
  const { shouldFocus, shouldPropagateVisibility, ignoreQueue } =
    action.payload;
  const file: ReturnType<typeof getActiveFile> = yield select(getActiveFile);
  const fileExt = getFileExtension(file?.filename ?? '');
  const isPdf = isFilePDF(fileExt);
  if (isPdf) {
    yield put(pdfViewer.reset());
  } else {
    // force focus if view is inactive
    const visibleBodies: ReturnType<typeof getVisibleBodies> = yield select(
      getVisibleBodies,
    );
    const forceFocus = visibleBodies.length == 0;

    yield put(
      cadViewer.receivePartFile({
        file,
        shouldFocus: shouldFocus || forceFocus,
        shouldPropagateVisibility: shouldPropagateVisibility || forceFocus,
        ignoreQueue,
      }),
    );
  }
}
